import { motion, AnimatePresence } from "motion/react";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: string;
  icon?: JSX.Element;
}

export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmColor = "#1B2A5B",
  icon,
}: ConfirmationModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-5"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="bg-white rounded-3xl p-6 w-full max-w-sm"
            style={{
              boxShadow:
                "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 100px rgba(27, 42, 91, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            {icon && (
              <div className="flex justify-center mb-5">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center"
                  style={{
                    background: `${confirmColor}10`,
                  }}
                >
                  {icon}
                </div>
              </div>
            )}

            {/* Title */}
            <h2
              className="text-[#0A0F1C] text-center mb-3"
              style={{
                fontSize: "20px",
                letterSpacing: "-0.02em",
                fontWeight: "700",
              }}
            >
              {title}
            </h2>

            {/* Message */}
            <p
              className="text-[#717182] text-center mb-6"
              style={{
                fontSize: "14px",
                fontWeight: "500",
                lineHeight: "1.5",
              }}
            >
              {message}
            </p>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="flex-1 py-3 px-4 rounded-xl border-2 border-[#E5E7EB] text-[#717182]"
                style={{ fontSize: "15px", fontWeight: "700" }}
              >
                {cancelText}
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                className="flex-1 py-3 px-4 rounded-xl text-white"
                style={{
                  backgroundColor: confirmColor,
                  fontSize: "15px",
                  fontWeight: "700",
                  boxShadow: `0 4px 12px ${confirmColor}50`,
                }}
              >
                {confirmText}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}